import { useEffect } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useSearchParams } from 'react-router-dom';
import { dismissToast, MDSSnackbar } from '@marqvision/mds-v2';
import { isValidSearch, usePrepareListSelecting, useQueryParams } from '@marqvision/shared';
import { listingTotalCountAtom } from '@marqvision/shared/src/hooks';
import { queryClient } from '~/apis';
import { QUERY_KEY_FILTER } from '~/apis/@constants';
import {
  useGetAllAssets,
  useGetAllDomains,
  useGetAllModels,
  useGetAllPtPlatforms,
  useGetAllPtSellers,
  useGetAllReasons,
  useGetAllReportable,
  useGetAllRules,
  useGetAllSellers,
} from '~/apis/enforcement/reporting';
import { useGetAllShipFromQuery, useTestPurchaseProgressFilterQuery } from '~/apis/monitoring/filter';
import { useClientBrandIds, useLogger } from '~/hooks';
import {
  MarqCommonEnumsPotentialAuthorizedReasonEnum,
  MarqCommonEnumsReportableReasonEnum,
  MarqCommonEnumsSortedOrderEnum,
  MarqCommonEnumsTpTpProgressStatusEnum,
} from '~/swagger/data-contracts';
import {
  getListingTabGroup,
  isEqualNumberArrays,
  isEqualStringArrays,
  mergePages,
  parseDropDown,
  parseValueToValueArray,
} from '~/utils';
import { clientFilterChipAtom } from '../@atoms';
import { FilterQueryParam, UpdatedDateRange } from '../@types';
import { FILTER_LOG_LABELS } from '../DynamicFilter/@constants';
import { useGetFilterParams, usePathParams } from './index';

// hooks for each filters
const STALE_TIME = 5000; // 5sec
export type KeywordFilterParams = {
  onSearch?: (value: string) => void;
};
export const useKeyword = ({ onSearch }: KeywordFilterParams) => {
  const { params, setParams, removeParams } = useQueryParams<FilterQueryParam>();
  const keywordParam = params.search || '';

  const search = (value: string) => {
    const { isValid, message } = isValidSearch(value);
    if (!isValid) {
      MDSSnackbar({
        type: 'error',
        title: message,
      });
      return false;
    } else {
      dismissToast();
    }
    if (value.trim() === '') {
      removeParams('search');
    } else {
      onSearch?.(value);
      setParams({ search: value }, { replace: true });
    }
  };

  return {
    value: keywordParam,
    search,
  };
};
export const useAssetOptions = () => {
  const { brandId } = useClientBrandIds();
  const { domainType, listingStatus } = usePathParams();
  const { params, requestFilterParams, setParams, removeParams } = useGetFilterParams();
  const { amplitudeLogging } = useLogger();
  const { assets: selectedAssetIds } = params;

  const assetQuery = useGetAllAssets(
    {
      ...requestFilterParams,
      asset_id_filter: [],
      brand_id: brandId,
      client_status_filter: listingStatus,
    },
    {
      enabled: !!listingStatus && listingStatus.length > 0,
      staleTime: STALE_TIME,
    }
  );

  const assetOptions = assetQuery.data?.list;

  const handleSelect = (value: number[]) => {
    if (!isEqualNumberArrays(value, parseValueToValueArray(selectedAssetIds))) {
      if (value.length > 0) {
        amplitudeLogging('applied filter', {
          Type: 'assets',
          Channel: domainType,
          Page: getListingTabGroup(listingStatus),
        });
        setParams({ assets: value }, { replace: true });
      } else {
        removeParams('assets');
      }
    }
  };

  return {
    list: assetOptions || [],
    totalCount: assetQuery.data?.count || 0,
    selected: parseValueToValueArray(selectedAssetIds), // to support MDSDropdown value type.
    select: handleSelect,
    disabled: assetOptions?.length === 0,
    isFetching: assetQuery.isFetching,
  };
};

export const usePlatformOptions = () => {
  const { domainType, listingStatus } = usePathParams();
  const { params, requestFilterParams, setParams, removeParams } = useGetFilterParams();
  const { platforms: selectedPlatformIds } = params;
  const { amplitudeLogging } = useLogger();

  const platformsQuery = useGetAllDomains(
    {
      ...requestFilterParams,
      domain_id_list: [],
    },
    {
      enabled: !!listingStatus && requestFilterParams.channel_type.length > 0,
      staleTime: STALE_TIME,
    }
  );

  const platformsOptions = platformsQuery.data?.list;
  const handleSelect = (value: number[]) => {
    if (!isEqualNumberArrays(value, parseValueToValueArray(selectedPlatformIds))) {
      if (value.length > 0) {
        amplitudeLogging('applied filter', {
          Type: 'marketplaces', // [PROD-11001] amplitude 전송 시에는 platforms로 바꾸지 않고 marketplaces로 유지
          Channel: domainType,
          Page: getListingTabGroup(listingStatus),
        });
        setParams({ platforms: value }, { replace: true });
      } else {
        removeParams('platforms');
      }
    }
  };

  return {
    list: platformsOptions || [],
    totalCount: platformsQuery.data?.count || 0,
    selected: parseValueToValueArray(selectedPlatformIds),
    select: handleSelect,
    disabled: platformsOptions?.length === 0,
    isFetching: platformsQuery.isFetching,
  };
};

export const useModelOptions = () => {
  const { params, requestFilterParams, setParams, removeParams } = useGetFilterParams();
  const { models: selectedModelIds } = params;
  const { amplitudeLogging } = useLogger();
  const { domainType, listingStatus } = usePathParams();

  const modelQuery = useGetAllModels(
    {
      ...requestFilterParams,
      model_id_filter: [],
    },
    {
      enabled: !!listingStatus,
      staleTime: STALE_TIME,
    }
  );

  const modelOptions = modelQuery.data?.list;
  const handleSelect = (value: number[]) => {
    if (!isEqualNumberArrays(value, parseValueToValueArray(selectedModelIds))) {
      if (value.length > 0) {
        amplitudeLogging('applied filter', {
          Type: 'models',
          Channel: domainType,
          Page: getListingTabGroup(listingStatus),
        });
        setParams({ models: value }, { replace: true });
      } else {
        removeParams('models');
      }
    }
  };

  return {
    list: modelOptions || [],
    totalCount: modelQuery.data?.count || 0,
    selected: parseValueToValueArray(selectedModelIds),
    select: handleSelect,
    disabled: modelOptions?.length === 0,
    isFetching: modelQuery.isFetching,
    shouldDisplayFilter: modelQuery.data?.filter_display,
  };
};

export const useSellerOptions = () => {
  const { params, requestPagingFilterParams, removeParams, setParams } = useGetFilterParams();
  const { domainType, listingStatus } = usePathParams();
  const [{ sellerNameList }, setAtom] = useAtom(clientFilterChipAtom);
  const { amplitudeLogging } = useLogger();

  const sellersQuery = useGetAllSellers(
    {
      ...requestPagingFilterParams,
      seller_name_filter: [],
    },
    { enabled: !!listingStatus, staleTime: STALE_TIME }
  );

  const sellerOptions = mergePages(sellersQuery.data?.pages);

  const handleSelect = (sellerNames: string[]) => {
    if (!isEqualStringArrays(sellerNames, sellerNameList)) {
      if (sellerNames.length > 0) {
        amplitudeLogging('applied filter', {
          Type: 'sellers',
          Channel: domainType,
          Page: getListingTabGroup(listingStatus),
        });
        setAtom((prev) => ({ ...prev, sellerNameList: sellerNames }));
      } else {
        setAtom((prev) => ({ ...prev, sellerNameList: [] }));
      }
    }
  };

  const handleSearch = (value: string) => {
    if (value !== params.seller_search_keyword) {
      if (value.trim() === '') {
        removeParams('seller_search_keyword');
      } else {
        const { isValid, message } = isValidSearch(value);
        if (!isValid) {
          MDSSnackbar({
            type: 'error',
            title: message,
          });
          return false;
        } else {
          dismissToast();
        }
        setParams({ seller_search_keyword: value }, { replace: true });
      }
    }
  };

  const handleFetchNextPage = () => {
    if (!sellersQuery.isFetching && !sellersQuery.isFetchingNextPage && sellersQuery.hasNextPage) {
      sellersQuery.fetchNextPage();
    }
  };

  useEffect(() => {
    return () => {
      queryClient.resetQueries([QUERY_KEY_FILTER, 'get-all-sellers']);
    };
  }, []);

  return {
    list: sellerOptions.list || [],
    selected: sellerNameList,
    hasNextPage: sellersQuery.hasNextPage,
    totalCount: sellersQuery.data?.pages[0].count,
    disabled: sellerOptions.list.length === 0,
    isLoading: sellersQuery.isLoading,
    isFetchingNextPage: sellersQuery.isFetchingNextPage,
    searchKeyword: params.seller_search_keyword,
    select: handleSelect,
    onSearch: handleSearch,
    fetchNextPage: handleFetchNextPage,
  };
};
export const useSmartRuleOptions = () => {
  const { params, requestFilterParams, setParams, removeParams } = useGetFilterParams();
  const { smart_rule_ids: selectedSmartRuleIds } = params;
  const { amplitudeLogging } = useLogger();
  const { domainType, listingStatus } = usePathParams();

  const smartRuleQuery = useGetAllRules(requestFilterParams, {
    enabled: !!requestFilterParams.client_status_filter,
    staleTime: STALE_TIME,
  });

  const smartRuleOptions = smartRuleQuery.data?.list;

  const handleSelect = (value: number[]) => {
    if (!isEqualNumberArrays(value, parseValueToValueArray(selectedSmartRuleIds))) {
      if (value.length > 0) {
        amplitudeLogging('applied filter', {
          Type: 'smart rule',
          Channel: domainType, // marketplace, social media..
          Page: getListingTabGroup(listingStatus), // scanned, flagged, in progress, case closed, test purchase
        });
        setParams({ smart_rule_ids: value }, { replace: true });
      } else {
        removeParams('smart_rule_ids');
      }
    }
  };

  return {
    list: smartRuleOptions ?? [],
    selected: requestFilterParams.enforcement_rule_id_filter ?? [],
    select: handleSelect,
    disabled: smartRuleOptions?.length === 0,
    isFetching: smartRuleQuery.isFetching,
  };
};
export const useUpdatedDate = () => {
  const totalCount = useAtomValue(listingTotalCountAtom);
  const { amplitudeLogging } = useLogger();
  const { domainType, listingStatus } = usePathParams();
  const [params, setParams] = useSearchParams();
  const dateRange: UpdatedDateRange = {
    startDate: params.get('start_date'),
    endDate: params.get('end_date'),
  };
  const setDateRange = ({ startDate, endDate }: UpdatedDateRange) => {
    amplitudeLogging('applied filter', {
      Type: 'updated date',
      Channel: domainType, // marketplace, social media..
      Page: getListingTabGroup(listingStatus), // scanned, flagged, in progress, case closed, test purchase
    });
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    setParams(params, { replace: true });
  };

  return {
    dateRange,
    setDateRange,
    disabled: totalCount === 0,
  };
};
export const useReportReason = () => {
  const { brandId } = useClientBrandIds();
  const { listingStatus, domainType } = usePathParams();
  const { params, requestFilterParams, setParams, removeParams } = useGetFilterParams();
  const { report_reason: reportReason } = params;
  const { amplitudeLogging } = useLogger();

  const reportReasonQuery = useGetAllReportable(
    {
      ...requestFilterParams,
      reportable_filter: [],
      brand_id: brandId,
      client_status_filter: listingStatus,
    },
    {
      enabled: !!listingStatus,
      staleTime: STALE_TIME,
    }
  );

  const reportReasonOptions = reportReasonQuery.data?.list;

  const handleSelect = (value: MarqCommonEnumsReportableReasonEnum[]) => {
    if (value.length > 0) {
      amplitudeLogging('applied filter', {
        Type: 'report reason',
        Channel: domainType, // marketplace, social media..
        Page: getListingTabGroup(listingStatus), // scanned, flagged, in progress, case closed, test purchase
      });
      setParams({ report_reason: value }, { replace: true });
    } else {
      removeParams('report_reason');
    }
  };

  return {
    list: reportReasonOptions || [],
    selected: parseValueToValueArray(reportReason),
    select: handleSelect,
    isFetching: reportReasonQuery.isFetching,
    disabled: reportReasonOptions?.length === 0,
  };
};

export const useReasonsOptions = () => {
  const { amplitudeLogging } = useLogger();
  const { domainType, listingStatus } = usePathParams();
  const { params, requestFilterParams, setParams, removeParams } = useGetFilterParams();
  const { potential_authorized_reason: selectedReasons } = params;

  const reasonsQuery = useGetAllReasons(
    {
      ...requestFilterParams,
      potential_authorized_reason_filter: [],
    },
    {
      enabled: !!listingStatus,
      staleTime: STALE_TIME,
    }
  );

  const reasonOptions = reasonsQuery.data?.list;

  const handleSelect = (value: (MarqCommonEnumsPotentialAuthorizedReasonEnum | null | undefined)[]) => {
    const filteredValue = value.filter(Boolean) as MarqCommonEnumsPotentialAuthorizedReasonEnum[];

    if (filteredValue.length > 0) {
      amplitudeLogging('applied filter', {
        Channel: domainType,
        Type: 'reasons',
        Page: getListingTabGroup(listingStatus),
      });

      setParams({ potential_authorized_reason: filteredValue }, { replace: true });
    } else {
      removeParams('potential_authorized_reason');
    }
  };

  return {
    list: reasonOptions || [],
    totalCount: reasonsQuery.data?.count || 0,
    selected: parseValueToValueArray(selectedReasons),
    select: handleSelect,
    disabled: reasonOptions?.length === 0,
    isFetching: reasonsQuery.isFetching,
    shouldDisplayFilter: reasonsQuery.data?.filter_display,
  };
};

export const usePtPlatformsOptions = () => {
  const { amplitudeLogging } = useLogger();
  const { listingStatus, domainType } = usePathParams();
  const { params, requestFilterParams, setParams, removeParams } = useGetFilterParams();
  const { platforms: selectedPlatformIds } = params;

  const platformsQuery = useGetAllPtPlatforms(
    {
      ...requestFilterParams,
      domain_id_list: [],
    },
    {
      enabled: !!listingStatus,
      staleTime: STALE_TIME,
    }
  );

  const platformsOptions = platformsQuery.data?.list;

  const handleSelect = (value: number[]) => {
    if (!isEqualNumberArrays(value, parseValueToValueArray(selectedPlatformIds))) {
      if (value.length > 0) {
        amplitudeLogging('applied filter', {
          Channel: domainType,
          Type: 'marketplaces',
          Page: getListingTabGroup(listingStatus),
        });

        setParams({ platforms: value }, { replace: true });
      } else {
        removeParams('platforms');
      }
    }
  };

  return {
    list: platformsOptions || [],
    totalCount: platformsQuery.data?.count || 0,
    selected: parseValueToValueArray(selectedPlatformIds),
    select: handleSelect,
    disabled: platformsOptions?.length === 0,
    isFetching: platformsQuery.isFetching,
    shouldDisplayFilter: platformsQuery.data?.filter_display,
  };
};

export const usePtSellerOptions = () => {
  const { listingStatus, domainType } = usePathParams();
  const { params, requestPagingFilterParams, setParams, removeParams } = useGetFilterParams();
  const [{ sellerNameList }, setAtom] = useAtom(clientFilterChipAtom);
  const { amplitudeLogging } = useLogger();

  const sellersQuery = useGetAllPtSellers(
    {
      ...requestPagingFilterParams,
      seller_name_filter: [],
    },
    {
      enabled: !!listingStatus,
      staleTime: STALE_TIME,
    }
  );

  const sellerOptions = sellersQuery.data?.list || [];

  const handleSelect = (sellerNames: string[]) => {
    if (!isEqualStringArrays(sellerNames, sellerNameList)) {
      if (sellerNames.length > 0) {
        amplitudeLogging('applied filter', {
          Channel: domainType,
          Type: 'sellers',
          Page: getListingTabGroup(listingStatus),
        });
        setAtom((prev) => ({ ...prev, sellerNameList: sellerNames }));
      } else {
        setAtom((prev) => ({ ...prev, sellerNameList: [] }));
      }
    }
  };
  const handleSearch = (value: string) => {
    if (value !== params.seller_search_keyword) {
      if (value.trim() === '') {
        removeParams('seller_search_keyword');
      } else {
        const { isValid, message } = isValidSearch(value);
        if (!isValid) {
          MDSSnackbar({
            type: 'error',
            title: message,
          });
          return false;
        } else {
          dismissToast();
        }
        setParams({ seller_search_keyword: value }, { replace: true });
      }
    }
  };

  useEffect(() => {
    return () => {
      queryClient.resetQueries([QUERY_KEY_FILTER, 'get-all-sellers']);
    };
  }, []);

  return {
    list: sellerOptions,
    selected: sellerNameList,
    totalCount: sellersQuery.data?.count,
    disabled: sellerOptions.length === 0,
    isFetching: sellersQuery.isFetching,
    searchKeyword: params.seller_search_keyword,
    select: handleSelect,
    search: handleSearch,
  };
};

export const useTpAssetOptions = () => {
  const { brandId } = useClientBrandIds();
  const { domainType, listingStatus } = usePathParams();
  const { params, requestFilterParams, setParams, removeParams } = useGetFilterParams();
  const { assets: selectedTpAssetIds } = params;
  const { amplitudeLogging } = useLogger();

  const tpAssetQuery = useGetAllAssets(
    {
      ...requestFilterParams,
      asset_id_filter: [],
      brand_id: brandId,
      client_status_filter: listingStatus,
    },
    {
      enabled: !!listingStatus,
      staleTime: STALE_TIME,
    }
  );

  const tpAssetOptions = tpAssetQuery.data?.list ?? [];

  const { resetListingState } = usePrepareListSelecting();

  const handleSelect = (value: number[]) => {
    if (!isEqualNumberArrays(value, parseValueToValueArray(selectedTpAssetIds))) {
      if (value.length > 0) {
        amplitudeLogging('applied filter', {
          Channel: domainType,
          Type: 'assets',
          Page: getListingTabGroup(listingStatus),
        });
        setParams({ assets: value }, { replace: true });
      } else {
        removeParams('assets');
      }
      resetListingState();
    }
  };

  return {
    list: tpAssetOptions || [],
    selected: parseValueToValueArray(selectedTpAssetIds),
    select: handleSelect,
    disabled: tpAssetOptions?.length === 0,
    isFetching: tpAssetQuery.isFetching,
  };
};
export const useTpPlatformsOptions = () => {
  const { brandId } = useClientBrandIds();
  const { listingStatus } = usePathParams();
  const { params, requestFilterParams, setParams, removeParams } = useGetFilterParams();
  const { platforms: selectedTpPlatformIds } = params;
  const { amplitudeLogging } = useLogger();

  const tpPlatformsQuery = useGetAllDomains(
    {
      ...requestFilterParams,
      domain_id_filter: [],
      brand_id: brandId,
      client_status_filter: listingStatus,
    },
    {
      enabled: !!listingStatus,
      staleTime: STALE_TIME,
    }
  );

  const tpMarketplaceOptions = tpPlatformsQuery.data?.list ?? [];

  const handleSelect = (value: number[]) => {
    if (!isEqualNumberArrays(value, parseValueToValueArray(selectedTpPlatformIds))) {
      if (value.length > 0) {
        amplitudeLogging('applied filter', {
          Type: 'marketplaces',
          On: 'enforcement',
        });
        setParams({ platforms: value }, { replace: true });
      } else {
        removeParams('platforms');
      }
    }
  };

  return {
    list: tpMarketplaceOptions || [],
    selected: parseValueToValueArray(selectedTpPlatformIds),
    select: handleSelect,
    disabled: tpMarketplaceOptions?.length === 0,
    isFetching: tpPlatformsQuery.isFetching,
  };
};
export const useTpSellerOptions = () => {
  const { brandId } = useClientBrandIds();
  const [{ sellerNameList }, setAtom] = useAtom(clientFilterChipAtom);
  const { listingStatus } = usePathParams();
  const { params, requestFilterParams, setParams, removeParams } = useGetFilterParams();
  const { amplitudeLogging } = useLogger();
  const tpSellersQuery = useGetAllSellers(
    {
      ...requestFilterParams,
      in_search_keyword_filter: params.seller_search_keyword,
      brand_id: brandId,
      client_status_filter: listingStatus,
      size: 30,
      search_after: [],
      sorted_order: MarqCommonEnumsSortedOrderEnum.Asc,
    },
    { enabled: !!listingStatus }
  );

  const tpSellerOptions = mergePages(tpSellersQuery.data?.pages).list ?? [];

  const handleSelect = (value: string[]) => {
    if (!isEqualStringArrays(value, sellerNameList)) {
      if (value.length > 0) {
        amplitudeLogging('applied filter', {
          Type: 'sellers',
          On: 'enforcement',
        });
        setAtom((prev) => ({ ...prev, sellerNameList: value }));
      } else {
        setAtom((prev) => ({ ...prev, sellerNameList: [] }));
      }
    }
  };

  const handleSearch = (value: string) => {
    if (value !== params.seller_search_keyword) {
      if (value.trim() === '') {
        removeParams('seller_search_keyword');
      } else {
        const { isValid, message } = isValidSearch(value);
        if (!isValid) {
          MDSSnackbar({
            type: 'error',
            title: message,
          });
          return false;
        } else {
          dismissToast();
        }
        setParams({ seller_search_keyword: value }, { replace: true });
      }
    }
  };

  const handleFetchNextPage = () => {
    if (!tpSellersQuery.isFetching && !tpSellersQuery.isFetchingNextPage) {
      tpSellersQuery.fetchNextPage();
    }
  };

  return {
    list: tpSellerOptions || [],
    selected: sellerNameList,
    select: handleSelect,
    hasNextPage: tpSellersQuery.hasNextPage,
    isFetchingNextPage: tpSellersQuery.isFetchingNextPage,
    disabled: tpSellerOptions?.length === 0,
    isFetching: tpSellersQuery.isFetching,
    searchKeyword: params.seller_search_keyword,
    totalCount: tpSellersQuery.data?.pages[0].count,
    onSearch: handleSearch,
    fetchNextPage: handleFetchNextPage,
  };
};

export const useShipFrom = () => {
  const { brandId } = useClientBrandIds();
  const { domainType, listingStatus } = usePathParams();
  const { params, requestFilterParams, setParams, removeParams } = useGetFilterParams();
  const { ship_from: shipFrom } = params;
  const { amplitudeLogging } = useLogger();

  const shipFromQuery = useGetAllShipFromQuery(
    {
      ...requestFilterParams,
      ship_from_filter: [],
      brand_id: brandId,
      client_status_filter: listingStatus,
    },
    {
      enabled: !!listingStatus,
      staleTime: STALE_TIME,
      select: parseDropDown<string>,
    }
  );

  const shipFromOptions = shipFromQuery.data?.list || [];

  const handleSelect = (value: string[]) => {
    if (isEqualStringArrays(value, parseValueToValueArray(shipFrom))) return;
    if (value.length > 0) {
      amplitudeLogging('applied filter', {
        Channel: domainType, // marketplace, social media..
        Type: 'ship from',
        Page: getListingTabGroup(listingStatus), // scanned, flagged, in progress, case closed, test purchase
      });
      setParams({ ship_from: value }, { replace: true });
    } else {
      removeParams('ship_from');
    }
  };

  return {
    list: shipFromOptions || [],
    totalCount: shipFromQuery.data?.count || 0,
    selected: parseValueToValueArray(shipFrom),
    select: handleSelect,
    isFetching: shipFromQuery.isFetching,
    disabled: shipFromOptions?.length === 0,
  };
};

export const useTestPurchaseProgressOptions = () => {
  const { brandId } = useClientBrandIds();
  const { domainType, listingStatus } = usePathParams();
  const { params, requestFilterParams, setParams, removeParams } = useGetFilterParams();
  const { tp_progress_status_filter } = params;
  const { amplitudeLogging } = useLogger();

  const testPurchaseProgressQuery = useTestPurchaseProgressFilterQuery(
    {
      ...requestFilterParams,
      tp_progress_status_filter: [],
      brand_id: brandId,
    },
    {
      enabled: !!listingStatus,
      staleTime: STALE_TIME,
      select: parseDropDown<MarqCommonEnumsTpTpProgressStatusEnum>,
    }
  );

  const testPurchaseProgressOptions = testPurchaseProgressQuery.data?.list || [];

  const handleSelect = (value: MarqCommonEnumsTpTpProgressStatusEnum[]) => {
    if (isEqualStringArrays(value, parseValueToValueArray(tp_progress_status_filter))) return;
    if (value.length > 0) {
      amplitudeLogging('applied filter', {
        Channel: domainType, // marketplace, social media..
        Type: FILTER_LOG_LABELS['Test Purchase progress'],
        Page: getListingTabGroup(listingStatus), // scanned, flagged, in progress, case closed, test purchase
      });
      setParams({ tp_progress_status_filter: value }, { replace: true });
    } else {
      removeParams('tp_progress_status_filter');
    }
  };

  return {
    list: testPurchaseProgressOptions,
    totalCount: testPurchaseProgressQuery.data?.count || 0,
    selected: parseValueToValueArray(tp_progress_status_filter),
    select: handleSelect,
    isFetching: testPurchaseProgressQuery.isFetching,
    disabled: testPurchaseProgressOptions.length === 0,
  };
};
//#endregion

export const useResetFilterState = () => {
  const { params, removeParams } = useQueryParams<FilterQueryParam>();
  const [clientFilterChipState, setClientFilterChipState] = useAtom(clientFilterChipAtom);

  const {
    keyword,
    search,
    assets,
    platforms,
    smart_rule_ids,
    report_reason,
    ois,
    high_risk,
    priority,
    use_of_rights,
    low_price,
    tm_logo,
    tm_logo_ids,
    tm_text,
    infringing_images_filter,
    repeat_offender,
    top_results,
    requested,
    min_price,
    max_price,
    start_date,
    end_date,
    flagged,
    models,
    custom_tag_id_filter,
    country,
    authorized_seller = null,
    max_sales_volume,
    min_sales_volume,
    likely_counterfeit,
    potential_authorized_reason,
    seller_search_keyword,
    ship_from,
    post_type_filter,
    country_filter,
    logo_detection_filter,
    scanned_date_start,
    scanned_date_end,
    domain_status_filter,
    favicon_detected_filter,
    added_date_start,
    added_date_end,
    requested_date_start,
    requested_date_end,
    ssl_cert_filter,
    mx_records_filter,
    comment_user_type_filter,
    tp_progress_status_filter,
    tp_reported_filter,
    domain_id_filter,
  } = params;
  const getOrFalse = (value?: boolean | string | null) => (value ? value === 'true' || value : false);

  const isFilterApplied = Boolean(
    keyword ||
      search ||
      !!assets ||
      !!platforms ||
      !!clientFilterChipState.sellerNameList.length ||
      !!smart_rule_ids ||
      !!report_reason ||
      !!ois ||
      !!tm_logo_ids ||
      flagged ||
      models ||
      country ||
      authorized_seller !== null ||
      seller_search_keyword ||
      custom_tag_id_filter ||
      getOrFalse(high_risk) ||
      getOrFalse(priority) ||
      getOrFalse(use_of_rights) ||
      getOrFalse(low_price) ||
      getOrFalse(tm_logo) ||
      getOrFalse(tm_text) ||
      getOrFalse(infringing_images_filter) ||
      getOrFalse(repeat_offender) ||
      getOrFalse(top_results) ||
      getOrFalse(requested) ||
      getOrFalse(likely_counterfeit) ||
      !!potential_authorized_reason ||
      !!min_price ||
      !!max_price ||
      !!start_date ||
      !!end_date ||
      !!min_sales_volume ||
      !!max_sales_volume ||
      !!ship_from ||
      !!post_type_filter ||
      !!country_filter ||
      getOrFalse(logo_detection_filter) ||
      !!scanned_date_start ||
      !!scanned_date_end ||
      domain_status_filter ||
      getOrFalse(favicon_detected_filter) ||
      added_date_start ||
      added_date_end ||
      requested_date_start ||
      requested_date_end ||
      getOrFalse(ssl_cert_filter) ||
      getOrFalse(mx_records_filter) ||
      !!comment_user_type_filter ||
      !!tp_progress_status_filter ||
      getOrFalse(tp_reported_filter) ||
      !!domain_id_filter
  );

  const resetAllFilters = () => {
    removeParams([
      'assets',
      'keyword',
      'search',
      'platforms',
      'smart_rule_ids',
      'report_reason',
      'high_risk',
      'priority',
      'use_of_rights',
      'low_price',
      'tm_logo',
      'tm_logo_ids',
      'tm_text',
      'infringing_images_filter',
      'repeat_offender',
      'top_results',
      'requested',
      'min_price',
      'max_price',
      'min_sales_volume',
      'max_sales_volume',
      'ois',
      'start_date',
      'end_date',
      'models',
      'country',
      'seller_search_keyword',
      'authorized_seller',
      'likely_counterfeit',
      'custom_tag_id_filter',
      'potential_authorized_reason',
      'ship_from',
      'post_type_filter',
      'country_filter',
      'logo_detection_filter',
      'scanned_date_start',
      'scanned_date_end',
      'domain_status_filter',
      'favicon_detected_filter',
      'added_date_start',
      'added_date_end',
      'requested_date_start',
      'requested_date_end',
      'ssl_cert_filter',
      'mx_records_filter',
      'comment_user_type_filter',
      'tp_progress_status_filter',
      'tp_reported_filter',
      'domain_id_filter',
    ]);

    setClientFilterChipState((prev) => ({
      ...prev,
      sellerNameList: [],
    }));
  };

  return {
    isFilterApplied,
    resetAllFilters,
  };
};
